<template>
  <!--
    STUDENT CHAT V2 -- CORE INTERACTION LOOP. The two V2 cards that follow a
    tutor answer:

    - `prompt`: V2 `.session-feedback.compact-feedback` -- «این بخش رو
      فهمیدی؟» with فهمیدم (`understood`) / نفهمیدم (`confused`). After a
      re-teach it uses V2's `--retry` copy («این بار واضح‌تر شد؟» / «هنوز
      نفهمیدم»).
    - `clarify`: V2 `.chat-recovery-card--question` -- shown when «نفهمیدم»
      was recorded but the tutor did not re-teach (upstream re-teaches only on
      a taught topic): the student writes what is unclear in the composer and
      it goes out as an ordinary turn. «بازگشت» returns to the prompt.

    V2's teacher-handoff choices are out of scope for this package.
  -->
  <section
    v-if="variant === 'clarify'"
    class="ai-tutor-feedback-recovery"
    data-testid="ai-tutor-feedback-clarify"
  >
    <div class="ai-tutor-feedback-recovery__head">
      <span><student-home-icon name="spark" :size="19"/></span>
      <div>
        <strong>{{ $t('aiTutorSetup.chat.feedback.clarifyTitle') }}</strong>
        <p>{{ $t('aiTutorSetup.chat.feedback.clarifyBody') }}</p>
      </div>
    </div>
    <button
      type="button"
      class="ai-tutor-feedback-recovery__back"
      data-testid="ai-tutor-feedback-back"
      @click="$emit('back')"
    >
      <student-home-icon name="arrow" :size="16" />
      {{ $t('aiTutorSetup.chat.feedback.back') }}
    </button>
  </section>

  <div
    v-else
    class="ai-tutor-feedback"
    :class="{ 'ai-tutor-feedback--retry': retry }"
    data-testid="ai-tutor-feedback-prompt"
    :aria-busy="busy ? 'true' : 'false'"
  >
    <div class="ai-tutor-feedback__copy">
      <strong>{{
        retry
          ? $t('aiTutorSetup.chat.feedback.retryQuestion')
          : $t('aiTutorSetup.chat.feedback.question')
      }}</strong>
      <span>{{
        retry
          ? $t('aiTutorSetup.chat.feedback.retryHint')
          : $t('aiTutorSetup.chat.feedback.hint')
      }}</span>
    </div>
    <div class="ai-tutor-feedback__actions">
      <button
        type="button"
        class="ai-tutor-feedback__understood"
        data-testid="ai-tutor-feedback-understood"
        :disabled="busy"
        @click="$emit('feedback', 'understood')"
      >
        <student-home-icon name="check" :size="16" />
        {{ $t('aiTutorSetup.chat.feedback.understood') }}
      </button>
      <button
        type="button"
        class="ai-tutor-feedback__not-understood"
        data-testid="ai-tutor-feedback-confused"
        :disabled="busy"
        @click="$emit('feedback', 'confused')"
      >
        {{
          retry
            ? $t('aiTutorSetup.chat.feedback.stillNotUnderstood')
            : $t('aiTutorSetup.chat.feedback.notUnderstood')
        }}
      </button>
    </div>
  </div>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'

export default {
  name: 'ChatFeedback',
  components: { StudentHomeIcon },
  props: {
    /** @type {import('vue').PropOptions<'prompt'|'clarify'>} */
    variant: {
      type: String,
      default: 'prompt',
      validator: value => ['prompt', 'clarify'].includes(value),
    },
    // The turn being rated is itself a «نفهمیدم» re-teach.
    retry: {
      type: Boolean,
      default: false,
    },
    // A «فهمیدم» is being recorded: both controls wait for it.
    busy: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
// Exact values from the canonical V2 prototype (sha256 6ddb695a...):
// `.session-feedback`, `.compact-feedback`, `.chat-recovery-card`,
// `.chat-recovery-head`, `.recovery-back--standalone`. Both cards sit under
// the tutor bubble, inset by the avatar (34px) + row gap (9px) = 43px, and
// go full width from 620px down.
$v2-warning-soft: #fff7e5;
$v2-warning-text: #855511;
$v2-warning-border: #efd397;
$v2-card-inset: 43px;

.ai-tutor-feedback {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin: 2px $v2-card-inset 0;
  padding: 11px 13px;
  border: 1px solid $borna-border;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 18px rgba(7, 20, 71, 0.04);
  line-height: 1.75;
}

.ai-tutor-feedback__copy {
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    color: $borna-text;
    font-size: 10px;
  }

  span {
    color: $borna-muted;
    font-size: 8px;
  }
}

.ai-tutor-feedback__actions {
  display: flex;
  gap: 6px;

  button {
    min-height: 32px;
    padding: 0 10px;
    border-radius: 9px;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
    cursor: pointer;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }
}

.ai-tutor-feedback__understood {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 0;
  background: $borna-indigo-600;
  color: #fff;
}

.ai-tutor-feedback__not-understood {
  border: 1px solid $v2-warning-border;
  background: $v2-warning-soft;
  color: $v2-warning-text;
}

.ai-tutor-feedback-recovery {
  margin: 0 $v2-card-inset;
  padding: 16px;
  border: 1px solid $borna-indigo-100;
  border-radius: 18px;
  background: linear-gradient(145deg, #f8faff, #fff);
  box-shadow: 0 8px 24px rgba(7, 20, 71, 0.05);
  line-height: 1.75;
}

.ai-tutor-feedback-recovery__head {
  display: grid;
  grid-template-columns: 38px 1fr;
  align-items: start;
  gap: 10px;

  > span {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: $borna-indigo-50;
    color: $borna-indigo-600;
  }

  strong {
    display: block;
    color: $borna-text;
    font-size: 12px;
  }

  p {
    margin: 3px 0 0;
    color: $borna-muted;
    font-size: 10px;
    line-height: 1.8;
  }
}

.ai-tutor-feedback-recovery__back {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  min-height: 40px;
  margin-top: 10px;
  padding: 0;
  border: 0;
  background: none;
  color: $borna-muted;
  font-family: inherit;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
}

// P43-02: Back is «→» in Persian and «←» in English; `arrow` is drawn «←».
html.is-rtl .ai-tutor-feedback-recovery__back ::v-deep svg {
  transform: scaleX(-1);
}

// V2 `@media (width<=620px)`.
@media (max-width: 620px) {
  .ai-tutor-feedback {
    flex-direction: column;
    align-items: flex-start;
    margin: 2px 0 0;
  }

  .ai-tutor-feedback__actions {
    width: 100%;

    button {
      flex: 1;
    }
  }

  .ai-tutor-feedback-recovery {
    margin: 0;
    padding: 14px;
  }
}
</style>
